import {
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import Masonry from "react-masonry-css";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { PopulatedDesignBoard, PopulatedProjectFile } from "../../__generated__/types";
import DashboardHeader from "../../components/dashboard-header";
import { useDesignerOnboardingAccess } from "../../hooks/useDesignerOnboardingAccess";
import { useGetBeforeAndAfterProjectsQuery } from "../../hooks/use-get-before-and-after-projects-query";
import { useGetDesignerProjectFiles } from "../../hooks/use-get-designer-project-files";
import { useGetMoodBoardsQuery } from "../../hooks/use-get-mood-boards-query";
import { useUserContext } from "../../hooks/userContext";
import {
  DesignBoardTypeLabels,
  Pages,
  budgetRangeOptions,
  colorFamilyOptions,
  finishOptions,
  formatSpecialRequirementLabel,
  patternLabels,
  patternOptions,
  projectCategoryOptions as projectCategoryOptionsList,
  projectCategoryLabels,
  projectStyleOptions,
  projectStyleLabels,
  specialRequirementsOptions,
  squareFootageOptions,
  textureOptions,
  textureLabels,
} from "../../utils";

type DesignerFileType = "project" | "before-after" | "design-board";
type FilterKey = "all" | DesignerFileType;

type DesignerFileCard = {
  id: string;
  groupId: string;
  imageUrl: string;
  altText: string;
  title: string;
  tags: string[];
  fileType: DesignerFileType;
  editPath: string;
  sortKey: number;
  projectCategory?: string | null;
  projectStyle?: string | null;
  budgetRange?: string | null;
  squareFootage?: string | null;
  specialRequirements?: string[] | null;
  designBoardType?: string | null;
  roomType?: string[] | null;
  pattern?: string[] | null;
  texture?: string[] | null;
  color?: string[] | null;
  finish?: string[] | null;
};


type TaggableProject = {
  projectStyle?: PopulatedProjectFile["projectStyle"] | null;
  style?: PopulatedProjectFile["style"] | null;
  projectCategory?: PopulatedProjectFile["projectCategory"] | null;
  specialRequirements?: PopulatedProjectFile["specialRequirements"] | null;
  budgetRange?: PopulatedProjectFile["budgetRange"] | null;
  squareFootage?: PopulatedProjectFile["squareFootage"] | null;
};

type ProjectFilterState = {
  projectTypes: string[];
  projectStyles: string[];
  budgetRanges: string[];
  squareFootages: string[];
  specialRequirements: string[];
};

type DesignBoardFilterState = {
  activeType: string | null;
  mood: {
    projectTypes: string[];
    projectStyles: string[];
    colors: string[];
  };
  color: {
    colors: string[];
  };
  texture: {
    textures: string[];
    finishes: string[];
  };
  pattern: {
    patterns: string[];
    colors: string[];
  };
  styleFusion: {
    patterns: string[];
    colors: string[];
  };
};

type DrawerView =
  | "root"
  | "before-after"
  | "project"
  | "design-boards"
  | "design-board-mood"
  | "design-board-color"
  | "design-board-texture"
  | "design-board-pattern"
  | "design-board-style-fusion";

const normalizeTagLabel = (label: string) => {
  const trimmed = label.replace(/\s*Design$/i, "").replace(/\s+/g, " ").trim();
  return trimmed || label;
};

const formatProjectCategoryLabel = (value: string | null | undefined) => {
  if (!value) return "";
  return (
    projectCategoryLabels[value as keyof typeof projectCategoryLabels] ??
    String(value)
  );
};

const getProjectTags = (project: TaggableProject) => {
  const tags: string[] = [];
  const styleValue = project.projectStyle ?? project.style;

  if (styleValue) {
    const styleLabel =
      projectStyleLabels[styleValue as keyof typeof projectStyleLabels] ??
      String(styleValue);
    tags.push(styleLabel);
  }

  if (project.projectCategory) {
    const categoryLabel = formatProjectCategoryLabel(project.projectCategory);
    if (categoryLabel) {
      tags.push(categoryLabel);
    }
  }

  (project.specialRequirements ?? []).forEach((requirement) => {
    if (!requirement) return;
    const requirementLabel = formatSpecialRequirementLabel(String(requirement));
    if (!requirementLabel) return;
    const normalizedLabel = requirementLabel.toLowerCase();
    if (normalizedLabel === "n/a" || normalizedLabel === "none") return;
    tags.push(requirementLabel);
  });

  const uniqueTags = Array.from(
    new Set(tags.map((tag) => normalizeTagLabel(tag)).filter(Boolean))
  );

  return uniqueTags.slice(0, 3);
};

const getDesignBoardTags = (board: PopulatedDesignBoard) => {
  const tags: string[] = [];

  if (board.type) {
    tags.push(
      DesignBoardTypeLabels[board.type as keyof typeof DesignBoardTypeLabels] ??
        String(board.type)
    );
  }

  const roomType = board.roomType?.find(Boolean);
  if (roomType) {
    tags.push(
      projectCategoryLabels[roomType as keyof typeof projectCategoryLabels] ??
        String(roomType)
    );
  }

  const pattern = board.pattern?.find(Boolean);
  if (pattern) {
    tags.push(
      patternLabels[pattern as keyof typeof patternLabels] ?? String(pattern)
    );
  }

  const texture = board.texture?.find(Boolean);
  if (texture) {
    tags.push(
      textureLabels[texture as keyof typeof textureLabels] ?? String(texture)
    );
  }

  const uniqueTags = Array.from(
    new Set(tags.map((tag) => normalizeTagLabel(tag)).filter(Boolean))
  );

  return uniqueTags.slice(0, 3);
};

const defaultProjectFilters: ProjectFilterState = {
  projectTypes: [],
  projectStyles: [],
  budgetRanges: [],
  squareFootages: [],
  specialRequirements: [],
};

const defaultDesignBoardFilters: DesignBoardFilterState = {
  activeType: null,
  mood: {
    projectTypes: [],
    projectStyles: [],
    colors: [],
  },
  color: {
    colors: [],
  },
  texture: {
    textures: [],
    finishes: [],
  },
  pattern: {
    patterns: [],
    colors: [],
  },
  styleFusion: {
    patterns: [],
    colors: [],
  },
};

const cloneProjectFilters = (filters: ProjectFilterState): ProjectFilterState => ({
  projectTypes: [...filters.projectTypes],
  projectStyles: [...filters.projectStyles],
  budgetRanges: [...filters.budgetRanges],
  squareFootages: [...filters.squareFootages],
  specialRequirements: [...filters.specialRequirements],
});

const cloneDesignBoardFilters = (
  filters: DesignBoardFilterState
): DesignBoardFilterState => ({
  activeType: filters.activeType,
  mood: {
    projectTypes: [...filters.mood.projectTypes],
    projectStyles: [...filters.mood.projectStyles],
    colors: [...filters.mood.colors],
  },
  color: {
    colors: [...filters.color.colors],
  },
  texture: {
    textures: [...filters.texture.textures],
    finishes: [...filters.texture.finishes],
  },
  pattern: {
    patterns: [...filters.pattern.patterns],
    colors: [...filters.pattern.colors],
  },
  styleFusion: {
    patterns: [...filters.styleFusion.patterns],
    colors: [...filters.styleFusion.colors],
  },
});

const toStringArray = (value: unknown) =>
  Array.isArray(value) ? value.filter(Boolean).map((entry) => String(entry)) : [];

const sanitizeProjectFilters = (value: any): ProjectFilterState => ({
  projectTypes: toStringArray(value?.projectTypes),
  projectStyles: toStringArray(value?.projectStyles),
  budgetRanges: toStringArray(value?.budgetRanges),
  squareFootages: toStringArray(value?.squareFootages),
  specialRequirements: toStringArray(value?.specialRequirements),
});

const sanitizeDesignBoardFilters = (value: any): DesignBoardFilterState => ({
  activeType: value?.activeType ? String(value.activeType) : null,
  mood: {
    projectTypes: toStringArray(value?.mood?.projectTypes),
    projectStyles: toStringArray(value?.mood?.projectStyles),
    colors: toStringArray(value?.mood?.colors),
  },
  color: {
    colors: toStringArray(value?.color?.colors),
  },
  texture: {
    textures: toStringArray(value?.texture?.textures),
    finishes: toStringArray(value?.texture?.finishes),
  },
  pattern: {
    patterns: toStringArray(value?.pattern?.patterns),
    colors: toStringArray(value?.pattern?.colors),
  },
  styleFusion: {
    patterns: toStringArray(value?.styleFusion?.patterns),
    colors: toStringArray(value?.styleFusion?.colors),
  },
});

const sortOptionsByLabel = <T extends { label: string }>(options: T[]) =>
  options.slice().sort((a, b) => a.label.localeCompare(b.label));

const toggleValue = (values: string[], value: string) => {
  if (values.includes(value)) {
    return values.filter((entry) => entry !== value);
  }
  return [...values, value];
};

const FILTER_LABELS: Record<FilterKey, string> = {
  all: "All Designer Files",
  "before-after": "Before & Afters",
  project: "Projects",
  "design-board": "Design Boards",
};

const PAGE_SIZE = 18;
const MASONRY_CARD_MIN_WIDTH = 215;
const MASONRY_GUTTER = 20;
const IMAGE_LOAD_TIMEOUT_MS = 8000;
const FILTER_STORAGE_KEY = "designer-files-filters";

const getSortKeyFromObjectId = (value?: string | null) => {
  if (!value) return 0;
  const hex = value.toString().slice(0, 8);
  const num = Number.parseInt(hex, 16);
  return Number.isNaN(num) ? 0 : num;
};

const mulberry32 = (seed: number) => {
  let t = seed;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), t | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const shuffleArray = <T,>(items: T[], random: () => number) => {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
};

const interleaveByGroup = <T,>(
  items: T[],
  getGroupId: (item: T) => string,
  seed: number,
  lastGroupId?: string | null
) => {
  if (items.length <= 1) return items;

  const groups = new Map<string, T[]>();
  items.forEach((item, index) => {
    const rawKey = getGroupId(item);
    const key = rawKey || `__ungrouped-${index}`;
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  });

  const keys = shuffleArray(Array.from(groups.keys()), mulberry32(seed));
  const result: T[] = [];
  let lastKey = lastGroupId ?? null;

  while (result.length < items.length && keys.length > 0) {
    let progressed = false;
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (key === lastKey && keys.length > 1) continue;
      const group = groups.get(key);
      if (!group || group.length === 0) {
        keys.splice(i, 1);
        i -= 1;
        continue;
      }
      result.push(group.shift() as T);
      lastKey = key;
      progressed = true;
      if (group.length === 0) {
        keys.splice(i, 1);
        i -= 1;
      }
    }

    if (!progressed && keys.length > 0) {
      const key = keys[0];
      const group = groups.get(key);
      if (!group || group.length === 0) {
        keys.splice(0, 1);
        continue;
      }
      result.push(group.shift() as T);
      lastKey = key;
      if (group.length === 0) {
        keys.splice(0, 1);
      }
    }
  }

  return result;
};

export const DesignerFilesProjectsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const shuffleSeedRef = useRef(Math.floor(Math.random() * 1_000_000));
  const { user } = useUserContext();
  const { isDesignerFilesUnlocked } = useDesignerOnboardingAccess();
  const { getDesignerProjectFiles, loading, designerProjectFiles } =
    useGetDesignerProjectFiles();
  const {
    getBeforeAndAfterProjects,
    loading: loadingBeforeAfter,
    beforeAndAfterProjects,
  } = useGetBeforeAndAfterProjectsQuery();
  const { getMoodBoards, loading: loadingMoodBoards, moodBoards } =
    useGetMoodBoardsQuery();
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [projectFilters, setProjectFilters] =
    useState<ProjectFilterState>(defaultProjectFilters);
  const [beforeAfterFilters, setBeforeAfterFilters] =
    useState<ProjectFilterState>(defaultProjectFilters);
  const [designBoardFilters, setDesignBoardFilters] =
    useState<DesignBoardFilterState>(defaultDesignBoardFilters);
  const [draftProjectFilters, setDraftProjectFilters] =
    useState<ProjectFilterState>(defaultProjectFilters);
  const [draftBeforeAfterFilters, setDraftBeforeAfterFilters] =
    useState<ProjectFilterState>(defaultProjectFilters);
  const [draftDesignBoardFilters, setDraftDesignBoardFilters] =
    useState<DesignBoardFilterState>(defaultDesignBoardFilters);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerView, setDrawerView] = useState<DrawerView>("root");
  const [expandedSection, setExpandedSection] = useState<string>("projectType");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === "undefined" ? 1200 : window.innerWidth
  );
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [trackImages, setTrackImages] = useState(true);
  const [imagesReady, setImagesReady] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasFetched = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const imageLoadSetRef = useRef<Set<string>>(new Set());
  const imageTotalRef = useRef(0);
  const loadedImageIdsRef = useRef<Set<string>>(new Set());
  const previousVisibleCountRef = useRef(visibleCount);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    if (!user?.id) return;
    hasFetched.current = true;
    getDesignerProjectFiles(user.id, true);
    getBeforeAndAfterProjects(user.id, true);
    getMoodBoards(user.id, true);
  }, [
    user?.id,
    getDesignerProjectFiles,
    getBeforeAndAfterProjects,
    getMoodBoards,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.activeFilter) {
          setActiveFilter(parsed.activeFilter as FilterKey);
        }
        if (parsed?.projectFilters) {
          setProjectFilters(sanitizeProjectFilters(parsed.projectFilters));
        }
        if (parsed?.beforeAfterFilters) {
          setBeforeAfterFilters(sanitizeProjectFilters(parsed.beforeAfterFilters));
        }
        if (parsed?.designBoardFilters) {
          setDesignBoardFilters(
            sanitizeDesignBoardFilters(parsed.designBoardFilters)
          );
        }
      } catch (error) {
        console.warn("Failed to restore designer files filters", error);
      }
    }
    setFiltersHydrated(true);
  }, []);

  useEffect(() => {
    if (!filtersHydrated) return;
    const params = new URLSearchParams(location.search);
    const filterParam = params.get("filter");
    const shouldClear = params.get("clear") === "1";
    if (!filterParam && !shouldClear) return;
    if (shouldClear) {
      const resetProject = cloneProjectFilters(defaultProjectFilters);
      const resetBefore = cloneProjectFilters(defaultProjectFilters);
      const resetDesign = cloneDesignBoardFilters(defaultDesignBoardFilters);
      setProjectFilters(resetProject);
      setBeforeAfterFilters(resetBefore);
      setDesignBoardFilters(resetDesign);
      setDraftProjectFilters(resetProject);
      setDraftBeforeAfterFilters(resetBefore);
      setDraftDesignBoardFilters(resetDesign);
    }
    if (
      filterParam === "project" ||
      filterParam === "before-after" ||
      filterParam === "design-board" ||
      filterParam === "all"
    ) {
      setActiveFilter(filterParam as FilterKey);
    }
  }, [filtersHydrated, location.search]);

  useEffect(() => {
    if (typeof window === "undefined" || !filtersHydrated) return;
    const payload = {
      activeFilter,
      projectFilters,
      beforeAfterFilters,
      designBoardFilters,
    };
    window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(payload));
  }, [
    activeFilter,
    projectFilters,
    beforeAfterFilters,
    designBoardFilters,
    filtersHydrated,
  ]);

  const projectTypeOptions = useMemo(
    () => sortOptionsByLabel(projectCategoryOptionsList),
    []
  );
  const projectStyleFilterOptions = useMemo(
    () => sortOptionsByLabel(projectStyleOptions),
    []
  );
  const budgetRangeFilterOptions = useMemo(
    () => sortOptionsByLabel(budgetRangeOptions),
    []
  );
  const squareFootageFilterOptions = useMemo(
    () => sortOptionsByLabel(squareFootageOptions),
    []
  );
  const specialRequirementsFilterOptions = useMemo(
    () => sortOptionsByLabel(specialRequirementsOptions),
    []
  );
  const colorFilterOptions = useMemo(
    () => sortOptionsByLabel(colorFamilyOptions),
    []
  );
  const textureFilterOptions = useMemo(
    () => sortOptionsByLabel(textureOptions),
    []
  );
  const patternFilterOptions = useMemo(
    () => sortOptionsByLabel(patternOptions),
    []
  );
  const finishFilterOptions = useMemo(
    () => sortOptionsByLabel(finishOptions),
    []
  );

  const projectFilterSelectionCount =
    projectFilters.projectTypes.length +
    projectFilters.projectStyles.length +
    projectFilters.budgetRanges.length +
    projectFilters.squareFootages.length +
    projectFilters.specialRequirements.length;
  const beforeAfterFilterSelectionCount =
    beforeAfterFilters.projectTypes.length +
    beforeAfterFilters.projectStyles.length +
    beforeAfterFilters.budgetRanges.length +
    beforeAfterFilters.squareFootages.length +
    beforeAfterFilters.specialRequirements.length;

  const projectCards = useMemo<DesignerFileCard[]>(() => {
    return (designerProjectFiles ?? []).flatMap((project) => {
      const isPublished = (project as PopulatedProjectFile | null | undefined)?.published ?? true;
      if (!isPublished) return [];
      const images = (project.images ?? []).filter(Boolean);
      const fallbackTitle = project.name?.trim() || "Project Name";
      const tags = getProjectTags(project);
      const editPath = `${Pages.ADD_DESIGNER_FILE__PROJECT_PROFILE}/${project._id}`;
      const projectSortKey = getSortKeyFromObjectId(String(project._id ?? ""));
      const groupId = `project-${project._id ?? "unknown"}`;

      if (images.length === 0) {
        return [
          {
            id: `project-${project._id}-empty`,
            groupId,
            imageUrl: "",
            altText: project.name ?? "Project image",
            title: fallbackTitle,
            tags,
            fileType: "project",
            editPath,
            sortKey: projectSortKey,
            projectCategory: project.projectCategory ?? null,
            projectStyle: project.projectStyle ?? project.style ?? null,
            budgetRange: project.budgetRange ?? null,
            squareFootage: project.squareFootage ?? null,
            specialRequirements: (project.specialRequirements ?? [])
              .filter(Boolean)
              .map((value) => String(value)),
          },
        ];
      }

      return images.map((image, index) => {
        const imageId = String(image?._id ?? "");
        return {
          id: `project-${imageId || `${project._id}-${index}`}`,
          groupId,
          imageUrl: image?.url ?? image?.path ?? "",
          altText: image?.title ?? project.name ?? "Project image",
          title: image?.title?.trim() || fallbackTitle,
          tags,
          fileType: "project",
          editPath,
          sortKey: getSortKeyFromObjectId(imageId) || projectSortKey,
          projectCategory: project.projectCategory ?? null,
          projectStyle: project.projectStyle ?? project.style ?? null,
          budgetRange: project.budgetRange ?? null,
          squareFootage: project.squareFootage ?? null,
          specialRequirements: (project.specialRequirements ?? [])
            .filter(Boolean)
            .map((value) => String(value)),
        };
      });
    });
  }, [designerProjectFiles]);

  const beforeAfterCards = useMemo<DesignerFileCard[]>(() => {
    return (beforeAndAfterProjects ?? []).flatMap((project) => {
      const isPublished = (project as any)?.published ?? true;
      if (!isPublished) return [];
      const fallbackTitle = project.name?.trim() || "Project Name";
      const tags = getProjectTags(project as TaggableProject);
      const editPath = `${Pages.ADD_DESIGNER_FILE__BEFORE_AFTER}/${project._id}`;
      const projectSortKey = getSortKeyFromObjectId(String(project._id ?? ""));
      const groupId = `before-after-${project._id ?? "unknown"}`;
      const afterImages = (project.afterImages ?? []).filter(Boolean);
      const imageCandidates = [
        project.coverImage,
        ...afterImages,
        project.afterImage,
      ].filter((image) => image?.url || image?.path);
      const cards: DesignerFileCard[] = [];
      const seen = new Set<string>();

      imageCandidates.forEach((image, index) => {
        const imageUrl = image?.url ?? image?.path ?? "";
        const imageId = String(image?._id ?? "");
        const key = imageId || imageUrl || `${project._id}-${index}`;
        if (seen.has(key)) return;
        seen.add(key);
        cards.push({
          id: `before-after-${key}`,
          groupId,
          imageUrl,
          altText: image?.title ?? project.name ?? "Before & after image",
          title: image?.title?.trim() || fallbackTitle,
          tags,
          fileType: "before-after",
          editPath,
          sortKey: getSortKeyFromObjectId(imageId) || projectSortKey,
          projectCategory: project.projectCategory ?? null,
          projectStyle:
            (project as TaggableProject).projectStyle ??
            (project as TaggableProject).style ??
            null,
          budgetRange: (project as any).budgetRange ?? null,
          squareFootage: (project as any).squareFootage ?? null,
          specialRequirements: (project as TaggableProject).specialRequirements
            ? (project as TaggableProject).specialRequirements
                ?.filter(Boolean)
                .map((value) => String(value))
            : [],
        });
      });

      if (cards.length > 0) return cards;

      return [
        {
          id: `before-after-${project._id}-empty`,
          groupId,
          imageUrl: "",
          altText: project.name ?? "Before & after image",
          title: fallbackTitle,
          tags,
          fileType: "before-after",
          editPath,
          sortKey: projectSortKey,
          projectCategory: project.projectCategory ?? null,
          projectStyle:
            (project as TaggableProject).projectStyle ??
            (project as TaggableProject).style ??
            null,
          budgetRange: (project as any).budgetRange ?? null,
          squareFootage: (project as any).squareFootage ?? null,
          specialRequirements: (project as TaggableProject).specialRequirements
            ? (project as TaggableProject).specialRequirements
                ?.filter(Boolean)
                .map((value) => String(value))
            : [],
        },
      ];
    });
  }, [beforeAndAfterProjects]);

  const designBoardCards = useMemo<DesignerFileCard[]>(() => {
    return (moodBoards ?? []).flatMap((board) => {
      const isPublished = (board as PopulatedDesignBoard | null | undefined)?.published ?? true;
      if (!isPublished) return [];
      const images = (board.images ?? []).filter(Boolean);
      const fallbackTitle = board.name?.trim() || "Design Board";
      const tags = getDesignBoardTags(board);
      const editPath = `${Pages.ADD_DESIGNER_FILE__BOARD}/${board._id}`;
      const boardSortKey = getSortKeyFromObjectId(String(board._id ?? ""));
      const groupId = `design-board-${board._id ?? "unknown"}`;

      if (images.length === 0) {
        return [
          {
            id: `design-board-${board._id}-empty`,
            groupId,
            imageUrl: "",
            altText: board.name ?? "Design board image",
            title: fallbackTitle,
            tags,
            fileType: "design-board",
            editPath,
            sortKey: boardSortKey,
            designBoardType: board.type ?? null,
            roomType: (board.roomType ?? []).filter(Boolean).map((value) => String(value)),
            pattern: (board.pattern ?? []).filter(Boolean).map((value) => String(value)),
            texture: (board.texture ?? []).filter(Boolean).map((value) => String(value)),
            color: (board.color ?? []).filter(Boolean).map((value) => String(value)),
            finish: (board.finish ?? []).filter(Boolean).map((value) => String(value)),
          },
        ];
      }

      return images.map((image, index) => {
        const imageId = String(image?._id ?? "");
        return {
          id: `design-board-${imageId || `${board._id}-${index}`}`,
          groupId,
          imageUrl: image?.url ?? image?.path ?? "",
          altText: image?.title ?? board.name ?? "Design board image",
          title: image?.title?.trim() || fallbackTitle,
          tags,
          fileType: "design-board",
          editPath,
          sortKey: getSortKeyFromObjectId(imageId) || boardSortKey,
          designBoardType: board.type ?? null,
          roomType: (board.roomType ?? []).filter(Boolean).map((value) => String(value)),
          pattern: (board.pattern ?? []).filter(Boolean).map((value) => String(value)),
          texture: (board.texture ?? []).filter(Boolean).map((value) => String(value)),
          color: (board.color ?? []).filter(Boolean).map((value) => String(value)),
          finish: (board.finish ?? []).filter(Boolean).map((value) => String(value)),
        };
      });
    });
  }, [moodBoards]);
  const matchesProjectFilters = useCallback(
    (card: DesignerFileCard, filters: ProjectFilterState) => {
      if (filters.projectTypes.length) {
        if (!card.projectCategory || !filters.projectTypes.includes(card.projectCategory)) {
          return false;
        }
      }
      if (filters.projectStyles.length) {
        const styleValue = card.projectStyle ?? "";
        if (!styleValue || !filters.projectStyles.includes(styleValue)) {
          return false;
        }
      }
      if (filters.budgetRanges.length) {
        if (!card.budgetRange || !filters.budgetRanges.includes(card.budgetRange)) {
          return false;
        }
      }
      if (filters.squareFootages.length) {
        if (!card.squareFootage || !filters.squareFootages.includes(card.squareFootage)) {
          return false;
        }
      }
      if (filters.specialRequirements.length) {
        const requirements = card.specialRequirements ?? [];
        if (!filters.specialRequirements.some((value) => requirements.includes(value))) {
          return false;
        }
      }
      return true;
    },
    []
  );

  const matchesDesignBoardFilters = useCallback(
    (card: DesignerFileCard, filters: DesignBoardFilterState) => {
      if (filters.activeType && card.designBoardType !== filters.activeType) {
        return false;
      }
      if (!filters.activeType) return true;

      const hasAny = (values: string[] | null | undefined, selected: string[]) => {
        if (!selected.length) return true;
        const list = values ?? [];
        return selected.some((value) => list.includes(value));
      };

      switch (filters.activeType) {
        case "Mood": {
          return (
            hasAny(card.roomType, filters.mood.projectTypes) &&
            hasAny(card.color, filters.mood.colors)
          );
        }
        case "Color": {
          return hasAny(card.color, filters.color.colors);
        }
        case "TextureAndFinish": {
          return (
            hasAny(card.texture, filters.texture.textures) &&
            hasAny(card.finish, filters.texture.finishes)
          );
        }
        case "PatternPlay": {
          return (
            hasAny(card.pattern, filters.pattern.patterns) &&
            hasAny(card.color, filters.pattern.colors)
          );
        }
        case "StyleFusion": {
          return (
            hasAny(card.pattern, filters.styleFusion.patterns) &&
            hasAny(card.color, filters.styleFusion.colors)
          );
        }
        default:
          return true;
      }
    },
    []
  );

  const projectCardsFiltered = useMemo(() => {
    return projectCards.filter((card) => matchesProjectFilters(card, projectFilters));
  }, [matchesProjectFilters, projectCards, projectFilters]);

  const beforeAfterCardsFiltered = useMemo(() => {
    return beforeAfterCards.filter((card) =>
      matchesProjectFilters(card, beforeAfterFilters)
    );
  }, [beforeAfterCards, beforeAfterFilters, matchesProjectFilters]);

  const designBoardCardsFiltered = useMemo(() => {
    return designBoardCards.filter((card) =>
      matchesDesignBoardFilters(card, designBoardFilters)
    );
  }, [designBoardCards, designBoardFilters, matchesDesignBoardFilters]);

  const projectCardsShuffled = useMemo(
    () =>
      interleaveByGroup(
        projectCardsFiltered,
        (card) => card.groupId,
        shuffleSeedRef.current + 1
      ),
    [projectCardsFiltered]
  );

  const beforeAfterCardsShuffled = useMemo(
    () =>
      interleaveByGroup(
        beforeAfterCardsFiltered,
        (card) => card.groupId,
        shuffleSeedRef.current + 2
      ),
    [beforeAfterCardsFiltered]
  );

  const designBoardCardsShuffled = useMemo(
    () =>
      interleaveByGroup(
        designBoardCardsFiltered,
        (card) => card.groupId,
        shuffleSeedRef.current + 3
      ),
    [designBoardCardsFiltered]
  );

  const designBoardFilterSelectionTotal =
    designBoardFilters.mood.projectTypes.length +
    designBoardFilters.mood.projectStyles.length +
    designBoardFilters.mood.colors.length +
    designBoardFilters.color.colors.length +
    designBoardFilters.texture.textures.length +
    designBoardFilters.texture.finishes.length +
    designBoardFilters.pattern.patterns.length +
    designBoardFilters.pattern.colors.length +
    designBoardFilters.styleFusion.patterns.length +
    designBoardFilters.styleFusion.colors.length;
  const allCards = useMemo(() => {
    return interleaveByGroup(
      [
        ...projectCardsShuffled,
        ...beforeAfterCardsShuffled,
        ...designBoardCardsShuffled,
      ],
      (card) => card.groupId,
      shuffleSeedRef.current + 4
    );
  }, [beforeAfterCardsShuffled, designBoardCardsShuffled, projectCardsShuffled]);

  const filteredCards = useMemo(() => {
    if (activeFilter === "all") return allCards;
    if (activeFilter === "project") {
      return projectCardsShuffled;
    }
    if (activeFilter === "before-after") {
      return beforeAfterCardsShuffled;
    }
    if (activeFilter === "design-board") {
      return designBoardCardsShuffled;
    }
    return allCards;
  }, [
    activeFilter,
    allCards,
    beforeAfterCardsShuffled,
    designBoardCardsShuffled,
    projectCardsShuffled,
  ]);

  const projectFilterKey = JSON.stringify(projectFilters);
  const beforeAfterFilterKey = JSON.stringify(beforeAfterFilters);
  const designBoardFilterKey = JSON.stringify(designBoardFilters);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeFilter, projectFilterKey, beforeAfterFilterKey, designBoardFilterKey]);

  const visibleCards = useMemo(
    () =>
      filteredCards
        .slice(0, visibleCount)
        .filter((card): card is DesignerFileCard => Boolean(card)),
    [filteredCards, visibleCount]
  );
  const isLoading = loading || loadingBeforeAfter || loadingMoodBoards;
  const showInitialSkeleton =
    isLoading ||
    (trackImages &&
      !imagesReady &&
      (!hasLoadedOnce || filteredCards.length === 0));
  const showLoadMoreSkeleton =
    hasLoadedOnce && (isLoadingMore || (trackImages && !imagesReady));
  const skeletonHeights = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, index) => {
        const heights = [220, 280, 240, 300, 260, 210, 290, 230, 270, 250];
        return heights[index % heights.length];
      }),
    []
  );
  const skeletonItems = useMemo(
    () =>
      skeletonHeights.map((height, index) => ({
        id: `designer-files-skeleton-${index}`,
        kind: "skeleton" as const,
        height,
      })),
    [skeletonHeights]
  );
  const loadMoreSkeletonHeights = useMemo(
    () =>
      Array.from({ length: 6 }).map((_, index) => {
        const heights = [220, 280, 240, 300, 260, 210];
        return heights[index % heights.length];
      }),
    []
  );
  const loadMoreSkeletonItems = useMemo(
    () =>
      loadMoreSkeletonHeights.map((height, index) => ({
        id: `designer-files-load-more-skeleton-${index}`,
        kind: "skeleton" as const,
        height,
      })),
    [loadMoreSkeletonHeights]
  );
  const masonryItems = useMemo(() => {
    const items = visibleCards.map((card) => ({
      id: card.id,
      kind: "card" as const,
      card,
    }));
    if (showLoadMoreSkeleton) {
      return [...items, ...loadMoreSkeletonItems];
    }
    return items;
  }, [loadMoreSkeletonItems, showLoadMoreSkeleton, visibleCards]);
  const masonryColumnSx = {
    width: "100%",
    "& .designer-files-masonry": {
      display: "flex",
      marginLeft: `-${MASONRY_GUTTER}px`,
      width: "auto",
    },
    "& .designer-files-masonry-column": {
      display: "flex",
      flexDirection: "column",
      paddingLeft: `${MASONRY_GUTTER}px`,
      gap: `${MASONRY_GUTTER}px`,
    },
  };
  const masonryColumns = useMemo(() => {
    const paddingEstimate = 64;
    const available = Math.max(0, viewportWidth - paddingEstimate);
    const columnWidth = MASONRY_CARD_MIN_WIDTH + MASONRY_GUTTER;
    return Math.max(1, Math.floor((available + MASONRY_GUTTER) / columnWidth));
  }, [viewportWidth]);
  const safeSkeletonItems = useMemo(
    () =>
      skeletonItems.filter(
        (item): item is typeof skeletonItems[number] =>
          Boolean(item) && typeof item === "object"
      ),
    [skeletonItems]
  );
  const safeMasonryItems = useMemo(
    () =>
      masonryItems.filter(
        (item): item is typeof masonryItems[number] =>
          Boolean(item) && typeof item === "object"
      ),
    [masonryItems]
  );
  const canLoadMore = visibleCount < filteredCards.length;

  useEffect(() => {
    if (!trackImages || isLoading) return;
    if (isLoadingMore && visibleCards.length < visibleCount) return;
    const loadedIds = new Set<string>();
    visibleCards.forEach((card) => {
      if (!card.imageUrl) return;
      if (loadedImageIdsRef.current.has(card.id)) {
        loadedIds.add(card.id);
      }
    });
    imageLoadSetRef.current = loadedIds;
    imageTotalRef.current = visibleCards.filter((card) => card.imageUrl).length;
    if (
      imageTotalRef.current === 0 ||
      loadedIds.size >= imageTotalRef.current
    ) {
      setImagesReady(true);
      setTrackImages(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [trackImages, isLoading, isLoadingMore, visibleCards, visibleCount]);

  useEffect(() => {
    if (!trackImages || isLoading || imagesReady) return;
    const timer = window.setTimeout(() => {
      setImagesReady(true);
      setTrackImages(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }, IMAGE_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [imagesReady, isLoading, trackImages]);

  const handleCardImageLoad = useCallback(
    (cardId: string) => {
      loadedImageIdsRef.current.add(cardId);
      if (!trackImages) return;
      if (imageLoadSetRef.current.has(cardId)) return;
      imageLoadSetRef.current.add(cardId);
      if (imageLoadSetRef.current.size >= imageTotalRef.current) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setImagesReady(true);
            setTrackImages(false);
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
          });
        });
      }
    },
    [trackImages]
  );

  useEffect(() => {
    if (hasLoadedOnce) return;
    if (!imagesReady) return;
    if (visibleCards.length === 0) return;
    setHasLoadedOnce(true);
  }, [hasLoadedOnce, imagesReady, visibleCards.length]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const handleRequestAppend = useCallback(() => {
    if (!canLoadMore) return;
    if (isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    const lastVisible = visibleCards[visibleCards.length - 1];
    if (lastVisible) {
      const el = document.querySelector<HTMLElement>(
        `[data-masonry-id="${lastVisible.id}"]`
      );
      if (el) {
        scrollAnchorRef.current = {
          id: lastVisible.id,
          top: el.getBoundingClientRect().top,
        };
      }
    }
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredCards.length));
  }, [canLoadMore, filteredCards.length, visibleCards]);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const scrollAnchorRef = useRef<{ id: string; top: number } | null>(null);

  useEffect(() => {
    if (!canLoadMore) return;
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleRequestAppend();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [canLoadMore, handleRequestAppend]);

  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (!anchor) return;
    const el = document.querySelector<HTMLElement>(
      `[data-masonry-id="${anchor.id}"]`
    );
    if (!el) return;
    const applyAnchor = () => {
      const nextTop = el.getBoundingClientRect().top;
      const delta = nextTop - anchor.top;
      if (delta !== 0) {
        window.scrollBy({ top: delta, left: 0 });
      }
    };
    applyAnchor();
    requestAnimationFrame(applyAnchor);
    setTimeout(applyAnchor, 50);
    setTimeout(() => {
      applyAnchor();
      scrollAnchorRef.current = null;
    }, 150);
  }, [visibleCount]);

  useEffect(() => {
    if (!canLoadMore) return;
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (!canLoadMore) return;
        const scrollBottom = window.innerHeight + window.scrollY;
        const threshold = document.body.offsetHeight - 800;
        if (scrollBottom >= threshold) {
          handleRequestAppend();
        }
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [canLoadMore, handleRequestAppend]);

  useEffect(() => {
    if (visibleCount <= previousVisibleCountRef.current) {
      previousVisibleCountRef.current = visibleCount;
      return;
    }
    previousVisibleCountRef.current = visibleCount;
    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    setTrackImages(true);
    setImagesReady(false);
    imageLoadSetRef.current = new Set();
    imageTotalRef.current = 0;
  }, [visibleCount]);

  const resolveDesignBoardType = (view: DrawerView) => {
    switch (view) {
      case "design-board-mood":
        return "Mood";
      case "design-board-color":
        return "Color";
      case "design-board-pattern":
        return "PatternPlay";
      case "design-board-style-fusion":
        return "StyleFusion";
      case "design-board-texture":
        return "TextureAndFinish";
      default:
        return null;
    }
  };

  const resolveDefaultSection = (view: DrawerView) => {
    switch (view) {
      case "before-after":
      case "project":
      case "design-board-mood":
        return "projectType";
      case "design-board-color":
        return "color";
      case "design-board-texture":
        return "texture";
      case "design-board-pattern":
      case "design-board-style-fusion":
        return "pattern";
      default:
        return "";
    }
  };

  const resolveFilterKeyFromDrawerView = (view: DrawerView): FilterKey => {
    if (view === "project") return "project";
    if (view === "before-after") return "before-after";
    if (view === "design-boards") return "design-board";
    if (view.startsWith("design-board")) return "design-board";
    return "all";
  };

  const openDrawer = (view: DrawerView) => {
    const nextProjectDraft = cloneProjectFilters(projectFilters);
    const nextBeforeAfterDraft = cloneProjectFilters(beforeAfterFilters);
    const nextDesignDraft = cloneDesignBoardFilters(designBoardFilters);
    const boardType = resolveDesignBoardType(view);
    if (boardType) {
      nextDesignDraft.activeType = boardType;
    }
    setDraftProjectFilters(nextProjectDraft);
    setDraftBeforeAfterFilters(nextBeforeAfterDraft);
    setDraftDesignBoardFilters(nextDesignDraft);
    setDrawerView(view);
    setExpandedSection(resolveDefaultSection(view));
    setDrawerOpen(true);
  };

  const navigateDrawer = (view: DrawerView) => {
    const boardType = resolveDesignBoardType(view);
    if (boardType) {
      setDraftDesignBoardFilters((prev) => ({
        ...prev,
        activeType: boardType,
      }));
    }
    setDrawerView(view);
    setExpandedSection(resolveDefaultSection(view));
  };

  const handleApplyFilters = () => {
    setProjectFilters(cloneProjectFilters(draftProjectFilters));
    setBeforeAfterFilters(cloneProjectFilters(draftBeforeAfterFilters));
    setDesignBoardFilters(cloneDesignBoardFilters(draftDesignBoardFilters));
    setDrawerOpen(false);
  };

  const resetAllFilters = () => {
    const resetProject = cloneProjectFilters(defaultProjectFilters);
    const resetBefore = cloneProjectFilters(defaultProjectFilters);
    const resetDesign = cloneDesignBoardFilters(defaultDesignBoardFilters);
    setProjectFilters(resetProject);
    setBeforeAfterFilters(resetBefore);
    setDesignBoardFilters(resetDesign);
    setDraftProjectFilters(resetProject);
    setDraftBeforeAfterFilters(resetBefore);
    setDraftDesignBoardFilters(resetDesign);
  };

  const handleClearFilters = () => {
    resetAllFilters();
    setActiveFilter("all");
  };

  useEffect(() => {
    if (activeFilter !== "all") return;
    resetAllFilters();
  }, [activeFilter]);

  const handleDrawerClose = () => setDrawerOpen(false);
  const handleDrawerBack = () => {
    if (
      drawerView === "design-board-mood" ||
      drawerView === "design-board-color" ||
      drawerView === "design-board-texture" ||
      drawerView === "design-board-pattern" ||
      drawerView === "design-board-style-fusion"
    ) {
      navigateDrawer("design-boards");
      return;
    }
    navigateDrawer("root");
  };

  const toggleProjectDraftValue = (
    field: keyof ProjectFilterState,
    value: string,
    target: "project" | "before-after"
  ) => {
    const setter =
      target === "project" ? setDraftProjectFilters : setDraftBeforeAfterFilters;
    setter((prev) => ({
      ...prev,
      [field]: toggleValue(prev[field], value),
    }));
  };

  const toggleDesignBoardDraftValue = (
    section: keyof DesignBoardFilterState,
    field: string,
    value: string
  ) => {
    if (section === "activeType") return;
    setDraftDesignBoardFilters((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] as Record<string, string[]>),
        [field]: toggleValue((prev[section] as Record<string, string[]>)[field], value),
      },
    }));
  };

  const filterButtonSx = (isActive: boolean) => ({
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 500,
    border: "1px solid",
    borderColor: isActive ? "#000" : "#D0D5DD",
    backgroundColor: "#fff",
    color: "#000",
    "&:hover": {
      borderColor: "#000",
      backgroundColor: "#F3F4F6",
    },
  });

  const handleSectionToggle = (section: string) => {
    setExpandedSection((prev) => (prev === section ? "" : section));
  };

  const renderSection = (
    label: string,
    sectionKey: string,
    options: { value: string; label: string }[],
    selectedValues: string[],
    onToggle: (value: string) => void
  ) => (
    <Box key={sectionKey}>
      <Box
        onClick={() => handleSectionToggle(sectionKey)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          cursor: "pointer",
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
        {expandedSection === sectionKey ? (
          <KeyboardArrowUpIcon />
        ) : (
          <KeyboardArrowDownIcon />
        )}
      </Box>
      <Collapse in={expandedSection === sectionKey} timeout="auto" unmountOnExit>
        <Stack spacing={1.5} sx={{ pb: 2 }}>
          {options.map((option) => (
            <Box
              key={option.value}
              sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
            >
              <Checkbox
                checked={selectedValues.includes(option.value)}
                onChange={() => onToggle(option.value)}
                sx={{
                  p: 0,
                  color: "#D1D5DB",
                  "&.Mui-checked": { color: "#111827" },
                }}
              />
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
      <Divider />
    </Box>
  );

  const renderNavItem = (label: string, onClick: () => void) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        cursor: "pointer",
      }}
    >
      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
      <ChevronRightIcon />
    </Box>
  );

  const getSelectionLabel = (label: string, count: number) => {
    if (!count) return label;
    return `${label} (${count})`;
  };

  const designBoardFilterSelectionCount = useMemo(() => {
    switch (designBoardFilters.activeType) {
      case "Mood":
        return (
          designBoardFilters.mood.projectTypes.length +
          designBoardFilters.mood.projectStyles.length +
          designBoardFilters.mood.colors.length
        );
      case "Color":
        return designBoardFilters.color.colors.length;
      case "TextureAndFinish":
        return (
          designBoardFilters.texture.textures.length +
          designBoardFilters.texture.finishes.length
        );
      case "PatternPlay":
        return (
          designBoardFilters.pattern.patterns.length +
          designBoardFilters.pattern.colors.length
        );
      case "StyleFusion":
        return (
          designBoardFilters.styleFusion.patterns.length +
          designBoardFilters.styleFusion.colors.length
        );
      default:
        return 0;
    }
  }, [designBoardFilters]);

  const projectButtonLabel = getSelectionLabel(
    FILTER_LABELS.project,
    projectFilterSelectionCount
  );
  const beforeAfterButtonLabel = getSelectionLabel(
    FILTER_LABELS["before-after"],
    beforeAfterFilterSelectionCount
  );
  const designBoardButtonLabel = getSelectionLabel(
    FILTER_LABELS["design-board"],
    designBoardFilterSelectionCount
  );

  const emptyState = useMemo(() => {
    switch (activeFilter) {
      case "before-after": {
        const title = beforeAfterFilterSelectionCount
          ? "No before & afters match the selected filters"
          : "No before & afters yet";
        const description = beforeAfterFilterSelectionCount
          ? "Try different filters or add a before & after project."
          : "Add a before & after project to showcase your transformations.";
        return {
          title,
          description,
          ctaLabel: "Add Before & After",
          ctaPath: Pages.ADD_DESIGNER_FILE__BEFORE_AFTER,
        };
      }
      case "project": {
        const title = projectFilterSelectionCount
          ? "No projects match the selected filters"
          : "No projects yet";
        const description = projectFilterSelectionCount
          ? "Try different filters or add a project file."
          : "Add a project file to showcase your work in this gallery.";
        return {
          title,
          description,
          ctaLabel: "Add Project File",
          ctaPath: Pages.ADD_DESIGNER_FILE__PROJECT_PROFILE,
        };
      }
      case "design-board":
        return {
          title: "No design boards yet",
          description: "Add a design board to highlight your concept work.",
          ctaLabel: "Add Design Board",
          ctaPath: Pages.ADD_DESIGNER_FILE__BOARD,
        };
      default:
        return {
          title: "No design files yet",
          description: "Add a designer file to showcase your work in this gallery.",
          ctaLabel: "Add Designer File",
          ctaPath: Pages.ADD_DESIGNER_FILE,
        };
    }
  }, [activeFilter, beforeAfterFilterSelectionCount, projectFilterSelectionCount]);

  const showAnySkeleton = showInitialSkeleton || showLoadMoreSkeleton;
  const resultsLabel =
    activeFilter === "project"
      ? projectButtonLabel
      : activeFilter === "before-after"
      ? beforeAfterButtonLabel
      : activeFilter === "design-board"
      ? designBoardButtonLabel
      : FILTER_LABELS[activeFilter];

  const drawerTitle = useMemo(() => {
    switch (drawerView) {
      case "root":
        return "Filter & Sort";
      case "before-after":
        return "Before & Afters";
      case "project":
        return "Projects";
      case "design-boards":
        return "Design Boards";
      case "design-board-mood":
        return "Mood Board";
      case "design-board-color":
        return "Color Board";
      case "design-board-texture":
        return "Texture & Finishes";
      case "design-board-pattern":
        return "Pattern Play";
      case "design-board-style-fusion":
        return "Style Fusion";
      default:
        return "Filter";
    }
  }, [drawerView]);

  const isDrawerListView = drawerView === "root" || drawerView === "design-boards";
  const showDrawerBack = drawerView !== "root";
  const showDrawerActions = !isDrawerListView;

  const drawerBody = (
    () => {
      switch (drawerView) {
        case "root":
          return (
            <Box>
              {renderNavItem("Before & Afters", () => navigateDrawer("before-after"))}
              <Divider />
              {renderNavItem("Project Type:", () => navigateDrawer("project"))}
              <Divider />
              {renderNavItem("Design Boards", () => navigateDrawer("design-boards"))}
              <Divider />
            </Box>
          );
        case "design-boards":
          return (
            <Box>
              {renderNavItem("Mood Board", () => navigateDrawer("design-board-mood"))}
              <Divider />
              {renderNavItem("Color Board", () => navigateDrawer("design-board-color"))}
              <Divider />
              {renderNavItem("Texture & Finishes", () =>
                navigateDrawer("design-board-texture")
              )}
              <Divider />
              {renderNavItem("Pattern Play", () => navigateDrawer("design-board-pattern"))}
              <Divider />
              {renderNavItem("Style Fusion", () =>
                navigateDrawer("design-board-style-fusion")
              )}
              <Divider />
            </Box>
          );
        case "project":
          return (
            <Box>
              {renderSection(
                "Project Type",
                "projectType",
                projectTypeOptions,
                draftProjectFilters.projectTypes,
                (value) => toggleProjectDraftValue("projectTypes", value, "project")
              )}
              {renderSection(
                "Project Style",
                "projectStyle",
                projectStyleFilterOptions,
                draftProjectFilters.projectStyles,
                (value) => toggleProjectDraftValue("projectStyles", value, "project")
              )}
              {renderSection(
                "Budget Range",
                "budgetRange",
                budgetRangeFilterOptions,
                draftProjectFilters.budgetRanges,
                (value) => toggleProjectDraftValue("budgetRanges", value, "project")
              )}
              {renderSection(
                "Square Footage",
                "squareFootage",
                squareFootageFilterOptions,
                draftProjectFilters.squareFootages,
                (value) => toggleProjectDraftValue("squareFootages", value, "project")
              )}
              {renderSection(
                "Special Requirements",
                "specialRequirements",
                specialRequirementsFilterOptions,
                draftProjectFilters.specialRequirements,
                (value) =>
                  toggleProjectDraftValue("specialRequirements", value, "project")
              )}
            </Box>
          );
        case "before-after":
          return (
            <Box>
              {renderSection(
                "Project Type",
                "projectType",
                projectTypeOptions,
                draftBeforeAfterFilters.projectTypes,
                (value) => toggleProjectDraftValue("projectTypes", value, "before-after")
              )}
              {renderSection(
                "Project Style",
                "projectStyle",
                projectStyleFilterOptions,
                draftBeforeAfterFilters.projectStyles,
                (value) =>
                  toggleProjectDraftValue("projectStyles", value, "before-after")
              )}
              {renderSection(
                "Budget Range",
                "budgetRange",
                budgetRangeFilterOptions,
                draftBeforeAfterFilters.budgetRanges,
                (value) =>
                  toggleProjectDraftValue("budgetRanges", value, "before-after")
              )}
              {renderSection(
                "Square Footage",
                "squareFootage",
                squareFootageFilterOptions,
                draftBeforeAfterFilters.squareFootages,
                (value) =>
                  toggleProjectDraftValue("squareFootages", value, "before-after")
              )}
              {renderSection(
                "Special Requirements",
                "specialRequirements",
                specialRequirementsFilterOptions,
                draftBeforeAfterFilters.specialRequirements,
                (value) =>
                  toggleProjectDraftValue(
                    "specialRequirements",
                    value,
                    "before-after"
                  )
              )}
            </Box>
          );
        case "design-board-mood":
          return (
            <Box>
              {renderSection(
                "Project Type",
                "projectType",
                projectTypeOptions,
                draftDesignBoardFilters.mood.projectTypes,
                (value) => toggleDesignBoardDraftValue("mood", "projectTypes", value)
              )}
              {renderSection(
                "Project Style",
                "projectStyle",
                projectStyleFilterOptions,
                draftDesignBoardFilters.mood.projectStyles,
                (value) => toggleDesignBoardDraftValue("mood", "projectStyles", value)
              )}
              {renderSection(
                "Color",
                "color",
                colorFilterOptions,
                draftDesignBoardFilters.mood.colors,
                (value) => toggleDesignBoardDraftValue("mood", "colors", value)
              )}
            </Box>
          );
        case "design-board-color":
          return (
            <Box>
              {renderSection(
                "Color",
                "color",
                colorFilterOptions,
                draftDesignBoardFilters.color.colors,
                (value) => toggleDesignBoardDraftValue("color", "colors", value)
              )}
            </Box>
          );
        case "design-board-texture":
          return (
            <Box>
              {renderSection(
                "Texture",
                "texture",
                textureFilterOptions,
                draftDesignBoardFilters.texture.textures,
                (value) => toggleDesignBoardDraftValue("texture", "textures", value)
              )}
              {renderSection(
                "Finishes",
                "finishes",
                finishFilterOptions,
                draftDesignBoardFilters.texture.finishes,
                (value) => toggleDesignBoardDraftValue("texture", "finishes", value)
              )}
            </Box>
          );
        case "design-board-pattern":
          return (
            <Box>
              {renderSection(
                "Pattern",
                "pattern",
                patternFilterOptions,
                draftDesignBoardFilters.pattern.patterns,
                (value) => toggleDesignBoardDraftValue("pattern", "patterns", value)
              )}
              {renderSection(
                "Color",
                "color",
                colorFilterOptions,
                draftDesignBoardFilters.pattern.colors,
                (value) => toggleDesignBoardDraftValue("pattern", "colors", value)
              )}
            </Box>
          );
        case "design-board-style-fusion":
          return (
            <Box>
              {renderSection(
                "Pattern",
                "pattern",
                patternFilterOptions,
                draftDesignBoardFilters.styleFusion.patterns,
                (value) => toggleDesignBoardDraftValue("styleFusion", "patterns", value)
              )}
              {renderSection(
                "Color",
                "color",
                colorFilterOptions,
                draftDesignBoardFilters.styleFusion.colors,
                (value) => toggleDesignBoardDraftValue("styleFusion", "colors", value)
              )}
            </Box>
          );
        default:
          return null;
      }
    }
  )();

  useEffect(() => {
    if (!isLoading) return;
    setTrackImages(true);
    setImagesReady(false);
    imageLoadSetRef.current = new Set();
    imageTotalRef.current = 0;
    setIsLoadingMore(false);
    isLoadingMoreRef.current = false;
  }, [isLoading]);

  if (user?.accountType === "Designer" && !isDesignerFilesUnlocked) {
    return (
      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            p: 4,
            maxWidth: "500px",
            textAlign: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Complete Your Bio First
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
            You need to complete your Designer Bio before you can access Designer Files.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(Pages.MY_BIO)}
            sx={{ backgroundColor: "#00A06D" }}
          >
            Go to My Bio
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#F5F5F5",
        minHeight: "100vh",
      }}
    >
      <DashboardHeader
        title="Designer Files"
        primaryAction={{
          label: "Add Designer File",
          onClick: () => navigate(Pages.ADD_DESIGNER_FILE),
          variant: "contained",
        }}
      />

      <Box sx={{ pb: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "nowrap", md: "wrap" },
            gap: 1.5,
            overflowX: { xs: "auto", md: "visible" },
            pb: { xs: 1, md: 0 },
            mb: 2,
            "&::-webkit-scrollbar": {
              height: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D1D5DB",
              borderRadius: 3,
            },
          }}
        >
          <Button
            onClick={() => {
              handleClearFilters();
              setActiveFilter("all");
            }}
            variant="outlined"
            disableElevation
            sx={filterButtonSx(activeFilter === "all")}
          >
            {FILTER_LABELS.all}
          </Button>

          <Button
            onClick={() => {
              setActiveFilter("before-after");
              resetAllFilters();
              setDrawerOpen(false);
            }}
            variant="outlined"
            disableElevation
            sx={filterButtonSx(activeFilter === "before-after")}
          >
            {beforeAfterButtonLabel}
          </Button>

          <Button
            onClick={() => {
              setActiveFilter("project");
              resetAllFilters();
              setDrawerOpen(false);
            }}
            variant="outlined"
            disableElevation
            sx={filterButtonSx(activeFilter === "project")}
          >
            {projectButtonLabel}
          </Button>

          <Button
            onClick={() => {
              setActiveFilter("design-board");
              resetAllFilters();
              setDrawerOpen(false);
            }}
            variant="outlined"
            disableElevation
            sx={filterButtonSx(activeFilter === "design-board")}
          >
            {designBoardButtonLabel}
          </Button>
          {activeFilter !== "all" && (
            <Button
              onClick={() => {
                if (activeFilter === "project") openDrawer("project");
                else if (activeFilter === "before-after") openDrawer("before-after");
                else if (activeFilter === "design-board") openDrawer("design-boards");
              }}
              variant="outlined"
              disableElevation
              startIcon={<FilterListIcon />}
              sx={{ ...filterButtonSx(false), ml: { xs: 0, md: "auto" } }}
            >
              Filter By
            </Button>
          )}
        </Box>

        {!showInitialSkeleton && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Showing {resultsLabel}, {visibleCards.length} of {filteredCards.length} Results
          </Typography>
        )}

        <Box sx={{ width: "100%", overflowAnchor: "none" }}>
          {filteredCards.length === 0 ? (
            showInitialSkeleton ? (
              <Box sx={masonryColumnSx}>
                <Masonry
                  breakpointCols={masonryColumns}
                  className="designer-files-masonry"
                  columnClassName="designer-files-masonry-column"
                >
                  {safeSkeletonItems.map((item, index) => (
                    <Box
                      key={item.id ?? `masonry-item-${index}`}
                      data-masonry-id={item.id}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        width: "100%",
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        height={item.kind === "skeleton" ? item.height : 220}
                      />
                      <Box sx={{ p: 2.5 }}>
                        <Skeleton variant="text" width="70%" height={26} />
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Skeleton variant="circular" width={28} height={28} />
                          <Skeleton variant="text" width="55%" height={20} />
                        </Stack>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1.5,
                          }}
                        >
                          <Skeleton variant="rounded" width={58} height={22} />
                          <Skeleton variant="rounded" width={48} height={22} />
                          <Skeleton variant="rounded" width={64} height={22} />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Masonry>
              </Box>
            ) : (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "16px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {emptyState.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {emptyState.description}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(emptyState.ctaPath)}
                >
                  {emptyState.ctaLabel}
                </Button>
              </Box>
            )
          ) : (
            <>
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    opacity: 1,
                    transition: "opacity 0.2s ease",
                    pointerEvents: showInitialSkeleton ? "none" : "auto",
                  }}
                >
              <Box sx={masonryColumnSx}>
                <Masonry
                  breakpointCols={masonryColumns}
                  className="designer-files-masonry"
                  columnClassName="designer-files-masonry-column"
                >
                {safeMasonryItems.map((item, index) => {
                  if (item.kind === "skeleton") {
                    return (
                      <Box
                        key={item.id ?? `masonry-item-${index}`}
                        sx={{
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
                          overflow: "hidden",
                          width: "100%",
                        }}
                      >
                        <Skeleton variant="rectangular" height={item.height} />
                        <Box sx={{ p: 2.5 }}>
                          <Skeleton variant="text" width="70%" height={26} />
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mt: 1 }}
                          >
                            <Skeleton variant="circular" width={28} height={28} />
                            <Skeleton variant="text" width="55%" height={20} />
                          </Stack>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: 1.5,
                            }}
                          >
                            <Skeleton variant="rounded" width={58} height={22} />
                            <Skeleton variant="rounded" width={48} height={22} />
                            <Skeleton variant="rounded" width={64} height={22} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  }

                  const card = item.card;
                  return (
                    <Box
                      key={item.id ?? `masonry-item-${index}`}
                      data-masonry-id={item.id}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        width: "100%",
                        "&:hover .designer-files-card-action, &:focus-within .designer-files-card-action":
                          {
                            opacity: 1,
                            transform: "translateY(0)",
                            pointerEvents: "auto",
                          },
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        {card.imageUrl ? (
                          <Box
                            component="img"
                            src={card.imageUrl}
                            alt={card.altText}
                            loading={showAnySkeleton ? "eager" : "lazy"}
                            onLoad={() => handleCardImageLoad(card.id)}
                            onError={() => handleCardImageLoad(card.id)}
                            sx={{
                              width: "100%",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              minHeight: 220,
                              backgroundColor: "#F3F4F6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ImageOutlinedIcon
                              sx={{ color: "#9CA3AF", fontSize: 40 }}
                            />
                          </Box>
                        )}

                        <Button
                          className="designer-files-card-action"
                          size="small"
                          variant="contained"
                          startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                          onClick={() => navigate(card.editPath)}
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            textTransform: "none",
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            color: "#1F2933",
                            fontWeight: 400,
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
                            opacity: 0,
                            pointerEvents: "none",
                            transform: "translateY(-4px)",
                            transition: "opacity 0.2s ease, transform 0.2s ease",
                            "@media (hover: none)": {
                              opacity: 1,
                              pointerEvents: "auto",
                              transform: "none",
                            },
                            "&:hover": {
                              backgroundColor: "#fff",
                            },
                          }}
                        >
                          Edit
                        </Button>
                      </Box>

                      <Box sx={{ p: 2.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#1F2933",
                            mb: 1,
                          }}
                        >
                          {card.title}
                        </Typography>

                        {card.tags.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: 1.5,
                            }}
                          >
                            {card.tags.map((tag) => (
                              <Chip
                                key={`${card.id}-${tag}`}
                                label={tag}
                                size="small"
                                sx={{
                                  borderRadius: "999px",
                                  backgroundColor: "#F3F4F6",
                                  color: "#6B7280",
                                  fontSize: "12px",
                                  textTransform: "lowercase",
                                  height: 24,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                </Masonry>
              </Box>
              {canLoadMore && (
                <Box
                  ref={loadMoreRef}
                  sx={{
                    height: 56,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  {isLoadingMore ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Button variant="text" onClick={handleRequestAppend}>
                      Load more
                    </Button>
                  )}
                </Box>
              )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "100%",
            overflowX: "hidden",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              {showDrawerBack ? (
                <IconButton onClick={handleDrawerBack} sx={{ color: "#111827" }}>
                  <ArrowBackIosNewIcon />
                </IconButton>
              ) : (
                <Box sx={{ width: 40 }} />
              )}
              <IconButton onClick={handleDrawerClose} sx={{ color: "#111827" }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
              {drawerTitle}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: "auto", px: 3, pb: 2 }}>
            {drawerBody}
          </Box>
          {showDrawerActions ? (
            <Box sx={{ px: 3, pb: 3, pt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                sx={{
                  backgroundColor: "#111827",
                  color: "#fff",
                  borderRadius: "10px",
                  py: 1.4,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#111827",
                  },
                }}
              >
                Apply
              </Button>
              <Button
                onClick={handleClearFilters}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  color: "#6B7280",
                  width: "100%",
                  textDecoration: "underline",
                }}
              >
                Clear Filters
              </Button>
            </Box>
          ) : (
            <Box sx={{ px: 3, pb: 3, pt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleDrawerClose}
                sx={{
                  backgroundColor: "#111827",
                  color: "#fff",
                  borderRadius: "10px",
                  py: 1.4,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#111827",
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};
